<script setup>
import iconThumbUp from '@manseImages/nangman-naming/icon-thumb-up.svg?url';
import iconTarget from '@manseImages/nangman-naming/icon-target.svg?url';
import iconMy from '@manseImages/nangman-naming/icon-my.svg?url';
import iconNotice from '@manseImages/nangman-naming/icon-notice.svg?url';
import { resolveOhaengBadge } from '@/utils/ohaengPalette';
import { useScrollLock } from '@/composables/useScrollLock';

const props = defineProps({
  isVisible: { type: Boolean, default: false },
  /** 내 사주 — 시/일/월/년 천간지지 + 핵심 오행 태그 */
  saju: {
    type: Object,
    default: () => ({ pillars: [], tags: [] }),
  },
  /**
   * 작명 기준 — rows 배열 기반.
   * 각 row: { label, value?, badges? }
   * - value만 있으면 텍스트, badges만 있으면 오행 배지 그룹.
   * - badges는 ['금', '토'] 라벨 문자열 배열 권장 (색은 ohaengPalette 룩업).
   *   { label, color, bg } 객체 형태도 호환.
   */
  criteria: {
    type: Object,
    default: () => ({
      rows: [
        { label: '성씨', value: '김' },
        { label: '성별', value: '남자' },
        { label: '용신오행', badges: ['금', '토'] },
        { label: '채점기준', value: '한글 · 5개 규칙 100점 만점' },
      ],
    }),
  },
  /** 적용 규칙 5개 */
  rules: {
    type: Array,
    default: () => [
      { label: '용신', point: 40, color: '#e65555', bg: 'rgba(230, 85, 85, 0.07)', desc: '사주 부족 오행 보충' },
      { label: '삼원오행', point: 40, color: '#74b75a', bg: 'rgba(116, 183, 90, 0.07)', desc: '이름 오행 상생 흐름' },
      { label: '조후', point: 10, color: '#4e80d6', bg: 'rgba(78, 128, 214, 0.07)', desc: '계절 기운 조절' },
      { label: '입묘', point: 6, color: '#f3ae11', bg: 'rgba(243, 174, 17, 0.07)', desc: '오행 갇힘 방지' },
      { label: '공망', point: 4, color: '#757575', bg: 'rgba(117, 117, 117, 0.07)', desc: '공망 충돌 방지' },
    ],
  },
});

const emit = defineEmits(['close']);

/** 팝업 노출 중 부모 스크롤 잠금 (PC/모바일 자동 분기) */
useScrollLock(computed(() => props.isVisible));

const close = () => emit('close');
</script>

<template>
  <Teleport to="body">
    <div v-if="isVisible" class="pop-criteria-backdrop" @click.self="close">
      <div class="pop-criteria">
        <!-- 헤더 -->
        <div class="pop-criteria__header">
          <div class="pop-criteria__title-wrap">
            <img :src="iconTarget" alt="" class="pop-criteria__title-icon" width="24" height="24" />
            <h3 class="pop-criteria__title">추천 기준</h3>
            <span class="pop-criteria__tag">이 기준으로 이름을 추천했어요</span>
          </div>
          <button type="button" class="pop-criteria__close" @click="close" aria-label="닫기">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 6L18 18M18 6L6 18"
                stroke="#333"
                stroke-width="1.8"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>

        <div class="pop-criteria__scroll">
        <!-- 내 사주 -->
        <section v-if="saju.pillars && saju.pillars.length" class="pop-criteria__section">
          <div class="pop-criteria__section-head">
            <img :src="iconMy" alt="" class="pop-criteria__section-icon" width="18" height="18" />
            <span class="pop-criteria__section-title">내 사주</span>
          </div>
          <div class="pop-criteria__pillars-labels">
            <span v-for="p in saju.pillars" :key="'l-' + p.label" class="pop-criteria__pillar-label">
              {{ p.label }}
            </span>
          </div>
          <div class="pop-criteria__pillars">
            <div v-for="p in saju.pillars" :key="'p-' + p.label" class="pop-criteria__pillar">
              <div class="pop-criteria__pillar-chars">
                <span :style="{ color: p.stemColor }">{{ p.stem }}</span>
                <span :style="{ color: p.branchColor }">{{ p.branch }}</span>
              </div>
              <span class="pop-criteria__pillar-kor">{{ p.kor }}</span>
            </div>
          </div>
          <div v-if="saju.tags && saju.tags.length" class="pop-criteria__saju-tags">
            <span v-for="t in saju.tags" :key="t.label" class="pop-criteria__saju-tag">
              {{ t.label }}
            </span>
          </div>
        </section>

        <!-- 작명 기준 — 데이터 있을 때만 (폴백 없음) -->
        <section v-if="criteria.rows && criteria.rows.length" class="pop-criteria__section">
          <div class="pop-criteria__section-head">
            <img :src="iconNotice" alt="" class="pop-criteria__section-icon" width="20" height="20" />
            <span class="pop-criteria__section-title">작명 기준</span>
          </div>
          <div class="pop-criteria__rows">
            <div
              v-for="(row, i) in criteria.rows"
              :key="row.label"
              class="pop-criteria__row"
              :class="{ 'pop-criteria__row--last': i === criteria.rows.length - 1 }"
            >
              <span class="pop-criteria__row-label">{{ row.label }}</span>
              <span v-if="row.value" class="pop-criteria__row-value">{{ row.value }}</span>
              <div v-else-if="row.badges" class="pop-criteria__row-badges">
                <span
                  v-for="(b, j) in row.badges.map(resolveOhaengBadge)"
                  :key="j"
                  class="pop-criteria__oh-badge"
                  :style="{ color: b.color, background: b.bg }"
                >
                  {{ b.label }}
                </span>
              </div>
            </div>
          </div>
        </section>

        <!-- 적용 규칙 — 데이터 있을 때만 (폴백 없음) -->
        <section v-if="rules && rules.length" class="pop-criteria__section">
          <div class="pop-criteria__section-head">
            <img :src="iconThumbUp" alt="" width="18" height="18" />
            <span class="pop-criteria__section-title">적용 규칙</span>
          </div>
          <ul class="pop-criteria__rules">
            <li v-for="r in rules" :key="r.label" class="pop-criteria__rule">
              <div class="pop-criteria__rule-left">
                <span class="pop-criteria__rule-label">{{ r.label }}</span>
                <span
                  class="pop-criteria__rule-point"
                  :style="{ color: r.color, background: r.bg }"
                >
                  {{ r.point }}점
                </span>
              </div>
              <span class="pop-criteria__rule-desc">{{ r.desc }}</span>
            </li>
          </ul>
        </section>

        <!-- 안내 -->
        <div class="pop-criteria__notice">
          <VIcon icon="mdi-information-outline" size="24" color="#724ce5" />
          <span class="pop-criteria__notice-text">
            추천 결과는 선택한 조건에 따라 달라질 수 있어요.
          </span>
        </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
$naming-accent-bright: #724ce5;
$naming-tag-bg: #f8f6ff;

.pop-criteria-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 9999;
  box-sizing: border-box;
}

.pop-criteria {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 432px;
  max-height: calc(100vh - 32px);
  border-radius: 16px;
  background: #fff;
  box-sizing: border-box;
  overflow: hidden; /* 스크롤은 .pop-criteria__scroll이 담당 (헤더 고정) */
}

/* 모바일: 풀스크린 시트로 전환 — 카드 여백/모서리 제거 (safe-area는 기본 규칙에서 항상 적용) */
@media (max-width: 768px) {
  .pop-criteria-backdrop {
    padding: 0;
  }

  .pop-criteria {
    width: 100%;
    max-width: none;
    height: 100%;
    max-height: 100vh;
    border-radius: 0;
  }

}

/* 헤더 — 고정 (스크롤 영역과 분리) */
.pop-criteria__header {
  flex-shrink: 0;
  background: #fff;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  /* safe-area를 @media 밖 기본 규칙에 둔다 — 앱 WebView는 --safe-area-top(JS 주입),
     웹은 env(). @media (max-width:768px)에 가두면 AutoHeightWebView가 viewport를
     width=device-width로 덮는 등 환경 차이로 보정이 통째로 누락될 수 있다. */
  padding: max(15px, var(--safe-area-top, 0px), env(safe-area-inset-top, 0px)) 16px 10px;
  box-sizing: border-box;
  min-height: 24px;
}

/* 스크롤 영역 — 헤더 아래만 스크롤 */
.pop-criteria__scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  width: 100%;
  padding: 15px 16px max(20px, var(--safe-area-bottom, 0px), env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.pop-criteria__scroll::-webkit-scrollbar {
  display: none;
}

.pop-criteria__title-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.pop-criteria__title {
  margin: 0 0 0 -4px;
  color: #333;
  font-family: Pretendard;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  white-space: nowrap;
}

.pop-criteria__tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 12px;
  background: $naming-tag-bg;
  color: #453674;
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  white-space: nowrap;
}

.pop-criteria__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
}

/* 섹션 공통 */
.pop-criteria__section {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;
  width: 100%;
  padding: 16px;
  border-radius: 15px;
  border: 1px solid #ebebeb;
  background: #fff;
  box-sizing: border-box;
}

.pop-criteria__section-head {
  display: flex;
  align-items: center;
  gap: 3px;
}

.pop-criteria__section-title {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
}

/* 내 사주 - 천간지지 */
.pop-criteria__pillars-labels {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
  text-align: center;
}

.pop-criteria__pillar-label {
  color: #575757;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
  line-height: 15px;
}

.pop-criteria__pillars {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
}

.pop-criteria__pillar {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
  min-width: 0;
  padding: 8px 4px;
  border-radius: 6px;
  background: #f8f8f8;
  box-sizing: border-box;
}

.pop-criteria__pillar-chars {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  font-family: 'ChosunGs', serif;
  font-size: 22px;
  font-weight: 400;
  line-height: 20px;
}

.pop-criteria__pillar-kor {
  color: #575757;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.pop-criteria__saju-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 4px;
}

.pop-criteria__saju-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 12px;
  background: $naming-tag-bg;
  color: $naming-accent-bright;
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 500;
  line-height: 16px;
}

/* 작명 기준 - 행 리스트 */
.pop-criteria__rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.pop-criteria__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 5px;
  border-bottom: 1px solid #ebebeb;
}

.pop-criteria__row--last {
  border-bottom: none;
}

.pop-criteria__row-label {
  color: #575757;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  flex-shrink: 0;
  white-space: nowrap;
}

.pop-criteria__row-value {
  flex: 1;
  min-width: 0;
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.4;
  text-align: right;
  word-break: keep-all;
}

.pop-criteria__row-badges {
  display: flex;
  gap: 5px;
}

.pop-criteria__oh-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  border-radius: 4px;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 600;
  line-height: 15px;
  box-sizing: border-box;
}

/* 적용 규칙 */
.pop-criteria__rules {
  display: flex;
  flex-direction: column;
  gap: 5px;
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
}

.pop-criteria__rule {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pop-criteria__rule-left {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 0 0 110px;
}

.pop-criteria__rule-label {
  color: #333;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 500;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.pop-criteria__rule-point {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  width: 40px;
  border-radius: 4px;
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 600;
  line-height: 12px;
  box-sizing: border-box;
  flex-shrink: 0;
}

.pop-criteria__rule-desc {
  flex: 1;
  min-width: 0;
  color: #575757;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.4;
  word-break: keep-all;
}

/* 안내 */
.pop-criteria__notice {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 16px;
  background: $naming-tag-bg;
}

.pop-criteria__notice-text {
  color: #333;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
}
</style>
